/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { SkeletonText, Tag, TagTypeName } from 'carbon-components-react';
import _ from 'lodash';
import React, { memo, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { useAppSelector } from '../../../../store';
import { EnvironmentType } from '../../../../ts/assistant';
import {
  areEnvironmentsLoading,
  selectEnvironmentById,
  selectEnvironmentIndexById
} from '../../store/environments/selectors';

import './EnvironmentTag.scss';

export interface EnvironmentTagProps {
  environmentId: string;
  fallbackEnvironmentName?: string;
  nameLength?: number;
}

/**
 *
 * @param {*} params the environmentId and the maximum nameLength. An environment
 * name longer than nameLength will truncate to nameLength - 1 and add '...', for
 * example, nameLength=5 will dislplay 'draft' entirely but name 'testing' will
 * display as 'test...' and 'accessibility' as 'acce...'
 * @returns
 */
const EnvironmentTagComponent: React.FC<EnvironmentTagProps> = ({
  environmentId,
  fallbackEnvironmentName,
  nameLength = 24
}) => {
  const { environmentName, environmentType } = useAppSelector(state => {
    const environment = selectEnvironmentById(state, environmentId);
    return {
      environmentName: environment?.name || fallbackEnvironmentName,
      environmentType: environment?.environment
    };
  });

  const environmentIndex = useAppSelector(state =>
    selectEnvironmentIndexById(state, environmentId)
  );
  const environmentsLoading = useAppSelector(areEnvironmentsLoading);

  const displayName = useMemo(() => {
    if (!environmentName) {
      return '';
    }
    let dName =
      environmentName.length <= nameLength
        ? environmentName
        : environmentName.substring(0, nameLength - 1) + '...';
    if (
      environmentType === EnvironmentType.LIVE ||
      environmentName === EnvironmentType.DRAFT
    ) {
      dName = _.capitalize(dName);
    }
    return dName;
  }, [environmentName, environmentType, nameLength]);

  const indexToColor = useMemo(() => {
    const envColors = ['cyan', 'magenta', 'teal'];
    if (!environmentIndex || environmentIndex < 1 || environmentIndex > 3) {
      return 'gray'; // if envs not loaded, orig code defaults to draft color = purple
    } else {
      return envColors[environmentIndex - 1];
    }
  }, [environmentIndex]);

  const type =
    environmentType === EnvironmentType.LIVE
      ? 'green'
      : environmentType === EnvironmentType.DRAFT
      ? 'purple'
      : indexToColor;

  return (
    <div className="EnvironmentTag">
      {!environmentsLoading ? (
        <Tag
          data-testid="environment-tag"
          id={`env_id_${environmentId}_${uuidv4()}`}
          type={type as TagTypeName}>
          {displayName}
        </Tag>
      ) : (
        <SkeletonText className="EnvironmentTag__loading" heading />
      )}
    </div>
  );
};

export const EnvironmentTag = memo(EnvironmentTagComponent);
EnvironmentTag.displayName = 'EnvironmentTag';
